/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2021] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

import { View } from 'backbone.marionette';
import Syphon from 'backbone.syphon';
import _ from 'lodash';

import { validateForm } from '@/util/ParsleyUtils';
import PhoneFormatBehavior from '@/behaviors/PhoneFormatBehavior';

import tmplSpecialtyRequirementDeliveryScheduleModalView from './SpecialtyRequirementDeliveryScheduleModalView.hbs';

const SpecialtyRequirementDeliveryScheduleModalView = View.extend({
  template: tmplSpecialtyRequirementDeliveryScheduleModalView,

  className: 'delivery-schedule-modal-view',

  ui: {
    nextBtn: '.nextBtn',
    prevBtn: '.prevBtn',
    copyBtn: '.copyBtn',
    saveBtn: '.saveBtn',
    checkbox: '.form-check-input',
    roomNumber: '#roomNumber',
    poleNumber: '#poleNumber',
    suite: '#suite',
    deliveryScheduleForm: '.delivery-schedule-form',
    itPOCSelect: '.itPOC-select',
    deliveryPOCSelect: '.deliveryPOC-select',
    itPOCName: '.itPOCName',
    itPOCEmail: '.itPOCEmail',
    itPOCPhone: '.itPOCPhone',
    deliveryPOCName: '.deliveryPOCName',
    deliveryPOCEmail: '.deliveryPOCEmail',
    deliveryPOCPhone: '.deliveryPOCPhone'
  },

  events: {
    'click @ui.copyBtn': 'copyPrevious',
    'click @ui.nextBtn': 'clickNext',
    'click @ui.prevBtn': 'clickPrev',
    'click @ui.saveBtn': 'clickSave',
    'change @ui.checkbox': 'changeCheckbox',
    'change @ui.itPOCSelect': 'changeITPOC',
    'change @ui.deliveryPOCSelect': 'changeDeliveryPOC'
  },

  behaviors: {
    telephone: PhoneFormatBehavior
  },

  changeCheckbox(e) {
    const $checkbox = this.$(e.currentTarget);
    const correspondingInput = $checkbox.attr('corresponding-input');
    const disabled = !$checkbox.prop('checked');
    this.ui[correspondingInput].prop('disabled', disabled);
    if (disabled) {
      this.ui[correspondingInput].val('');
    }
  },

  changeITPOC(e) {
    e.preventDefault();
    const itPOCId = this.ui.itPOCSelect.val();
    const currPOC = this.model.get('commandPOCs').itPOCs.find((poc) => poc.id === itPOCId);

    if (itPOCId) {
      const itPOCName = `${currPOC.firstName} ${currPOC.lastName}`;
      const itPOCEmail = currPOC.email;
      const itPOCPhone = currPOC.telephone;
      this.ui.itPOCName.val(itPOCName);
      this.ui.itPOCEmail.val(itPOCEmail);
      this.ui.itPOCPhone.val(itPOCPhone);
    } else {
      this.ui.itPOCName.val('');
      this.ui.itPOCEmail.val('');
      this.ui.itPOCPhone.val('');
    }
  },

  changeDeliveryPOC(e) {
    e.preventDefault();
    const deliveryPOCId = this.ui.deliveryPOCSelect.val();
    const currPOC = this.model.get('commandPOCs').allPOCs.find((poc) => poc.id === deliveryPOCId);

    if (deliveryPOCId) {
      const deliveryPOCName = `${currPOC.firstName} ${currPOC.lastName}`;
      const deliveryPOCEmail = currPOC.email;
      const deliveryPOCPhone = currPOC.telephone;
      this.ui.deliveryPOCName.val(deliveryPOCName);
      this.ui.deliveryPOCEmail.val(deliveryPOCEmail);
      this.ui.deliveryPOCPhone.val(deliveryPOCPhone);
    } else {
      this.ui.deliveryPOCName.val('');
      this.ui.deliveryPOCEmail.val('');
      this.ui.deliveryPOCPhone.val('');
    }
  },

  toggleForm(state) {
    this.$el.find(':input').prop('disabled', !state);
  },

  templateContext() {
    return {
      isReadOnly: this.options.isReadOnly,
      itPOCExists: this.options.itPOCExists,
      isPriceVisible: this.options.isPriceVisible,
      baseOrg: this.options.baseOrg,
      isInternal: !EXTERNAL,
      hasAdditionalServices: false
    };
  },

  onRender() {
    this.ui.checkbox.each((i, checkbox) => {
      this.changeCheckbox({ currentTarget: checkbox });
    });

    if (this.options.isReadOnly) {
      this.toggleForm(false);
      this.ui.nextBtn.prop('disabled', false);
      this.ui.prevBtn.prop('disabled', false);
    }

    const curIndex = _.findIndex(this.model.collection.models, ['id', this.model.get('id')]);
    const maxIndex = this.model.collection.length - 1;
    if (curIndex === 0 || curIndex === -1) {
      this.ui.copyBtn.hide();
      this.ui.prevBtn.hide();
    }
    if (curIndex === maxIndex || curIndex === -1) {
      this.ui.nextBtn.hide();
    }
  },

  copyPrevious(e) {
    e.preventDefault();
    const curIndex = _.findIndex(this.model.collection.models, ['id', this.model.get('id')]);
    const prevIndex = curIndex - 1;
    const prevModel = this.model.collection.models[prevIndex];
    if (prevModel) {
      Syphon.deserialize(this, prevModel.toJSON());
      this.ui.checkbox.each((i, checkbox) => {
        this.changeCheckbox({ currentTarget: checkbox });
      });
      Syphon.deserialize(this, prevModel.toJSON());
    }
  },

  async clickNext() {
    await validateForm(this.ui.deliveryScheduleForm);
    const curIndex = _.findIndex(this.model.collection.models, ['id', this.model.get('id')]);
    const nextIndex = curIndex + 1;
    const formData = this.getFormData();
    this.triggerMethod('modal:close', { nextIndex, button: 'next', formData });
  },

  async clickPrev() {
    await validateForm(this.ui.deliveryScheduleForm);
    const curIndex = _.findIndex(this.model.collection.models, ['id', this.model.get('id')]);
    const nextIndex = curIndex - 1;
    const formData = this.getFormData();
    this.triggerMethod('modal:close', { nextIndex, button: 'prev', formData });
  },

  async clickSave() {
    await validateForm(this.ui.deliveryScheduleForm);
    const formData = this.getFormData();
    this.triggerMethod('modal:close', { button: 'save', formData });
  },

  getFormData() {
    const formData = Syphon.serialize(this);
    const address = formData.address;

    if (address) {
      address.buildingFloorNumber =
        address.buildingFloorNumber && !address.buildingFloorNumber.match(/^BLDG /)
          ? 'BLDG '.concat(address.buildingFloorNumber)
          : address.buildingFloorNumber;
      address.floorNumber =
        address.floorNumber && !address.floorNumber.match(/^FL /)
          ? 'FL '.concat(address.floorNumber)
          : address.floorNumber;
      address.roomNumber =
        address.roomNumber && !address.roomNumber.match(/^RM /) ? 'RM '.concat(address.roomNumber) : address.roomNumber;
      address.poleNumber =
        address.poleNumber && !address.poleNumber.match(/^POLE /)
          ? 'POLE '.concat(address.poleNumber)
          : address.poleNumber;
      address.suite = address.suite && !address.suite.match(/^STE /) ? 'STE '.concat(address.suite) : address.suite;

      address.roomPoleNumber = [address.roomNumber, address.poleNumber, address.suite].filter((x) => x).join(' ');
    }

    return formData;
   }
});

export default SpecialtyRequirementDeliveryScheduleModalView;
